import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchAssessmentById,
  fetchAssessmentEntries,
  saveAssessmentEntry,
  updateAssessmentStatus,
  type SaveAssessmentEntryPayload
} from "@/services/repositories/assessment-repository";
import type { Assessment, AssessmentEntry, AssessmentStatus } from "@/types/assessment";

export type ScaleAssessmentSaveStatus = "idle" | "saving" | "saved" | "error";

export function useScaleAssessmentSession(
  requestedAssessmentId: string | undefined,
  expectedStudentId?: string,
): {
  assessment?: Assessment;
  assessmentLoading: boolean;
  assessmentError: unknown;
  entries: AssessmentEntry[];
  entriesLoading: boolean;
  saveEntry: (
    payload: Omit<SaveAssessmentEntryPayload, "assessmentId" | "expectedVersion">,
  ) => Promise<void>;
  flushEntries: () => Promise<void>;
  saveStatus: ScaleAssessmentSaveStatus;
  markStatus: (status: AssessmentStatus) => Promise<void>;
  marking: boolean;
} {
  const queryClient = useQueryClient();
  const [saveStatus, setSaveStatus] = useState<ScaleAssessmentSaveStatus>("idle");
  const saveQueues = useRef(new Map<string, Promise<void>>());

  const assessmentQuery = useQuery({
    queryKey: ["assessments", requestedAssessmentId],
    enabled: Boolean(requestedAssessmentId),
    queryFn: () => fetchAssessmentById(requestedAssessmentId!),
    staleTime: Infinity
  });

  const assessmentId =
    assessmentQuery.data &&
    (!expectedStudentId || assessmentQuery.data.studentId === expectedStudentId)
      ? assessmentQuery.data.id
      : undefined;

  const entriesQuery = useQuery({
    queryKey: ["assessmentEntries", assessmentId],
    enabled: Boolean(assessmentId),
    queryFn: () =>
      assessmentId ? fetchAssessmentEntries(assessmentId) : Promise.resolve<AssessmentEntry[]>([]),
    staleTime: 30 * 1000
  });

  const saveEntryMutation = useMutation({
    mutationFn: (payload: SaveAssessmentEntryPayload) => saveAssessmentEntry(payload),
    onMutate: () => setSaveStatus("saving"),
    onSuccess: (entry) => {
      queryClient.setQueryData<AssessmentEntry[] | undefined>(
        ["assessmentEntries", entry.assessmentId],
        (previous) => {
          const list = previous ? [...previous] : [];
          const index = list.findIndex((item) => item.entryCode === entry.entryCode);
          if (index >= 0) {
            list[index] = entry;
          } else {
            list.push(entry);
          }
          return list;
        },
      );
      setSaveStatus("saved");
    },
    onError: () => setSaveStatus("error"),
    onSettled: () => {
      setTimeout(() => setSaveStatus("idle"), 1500);
    }
  });

  const markStatusMutation = useMutation({
    mutationFn: (status: AssessmentStatus) => {
      if (!assessmentId) {
        return Promise.resolve(undefined);
      }
      return updateAssessmentStatus(assessmentId, status);
    },
    onSuccess: async (updated) => {
      if (updated) {
        queryClient.setQueryData(["assessments", updated.id], updated);
        await queryClient.invalidateQueries({ queryKey: ["assessments"] });
      }
    }
  });

  const entries = useMemo(() => entriesQuery.data ?? [], [entriesQuery.data]);

  const saveEntry = useCallback(
    (payload: Omit<SaveAssessmentEntryPayload, "assessmentId" | "expectedVersion">) => {
      if (!assessmentId) {
        return Promise.reject(new Error("评估记录尚未就绪"));
      }
      const previous = saveQueues.current.get(payload.entryCode) ?? Promise.resolve();
      const next = previous
        .catch(() => undefined)
        .then(async () => {
          const cached =
            queryClient.getQueryData<AssessmentEntry[]>(["assessmentEntries", assessmentId]) ?? [];
          const current = cached.find((entry) => entry.entryCode === payload.entryCode);
          const expectedVersion = current?.version ?? 0;
          await saveEntryMutation.mutateAsync({
            assessmentId,
            ...payload,
            score: payload.score === undefined ? (current?.score ?? null) : payload.score,
            comment: payload.comment === undefined ? (current?.comment ?? "") : payload.comment,
            expectedVersion
          });
        });
      saveQueues.current.set(payload.entryCode, next);
      const cleanup = () => {
        if (saveQueues.current.get(payload.entryCode) === next) {
          saveQueues.current.delete(payload.entryCode);
        }
      };
      void next.then(cleanup, cleanup);
      return next;
    },
    [assessmentId, queryClient, saveEntryMutation],
  );

  const flushEntries = useCallback(async () => {
    await Promise.all([...saveQueues.current.values()]);
  }, []);

  const markStatus = useCallback(
    async (status: AssessmentStatus) => {
      await flushEntries();
      await markStatusMutation.mutateAsync(status);
    },
    [flushEntries, markStatusMutation],
  );

  return {
    assessment: assessmentQuery.data,
    assessmentLoading: assessmentQuery.isLoading,
    assessmentError: assessmentQuery.error,
    entries,
    entriesLoading: entriesQuery.isLoading,
    saveEntry,
    flushEntries,
    saveStatus,
    markStatus,
    marking: markStatusMutation.isPending
  };
}
